import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import {
  LessonLearningResponse,
  LessonQuizResultSummary,
  LearningQuestion
} from '../../core/models/portal.models';

@Component({
  selector: 'app-knowledge-check-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './knowledge-check-page.component.html'
})
export class KnowledgeCheckPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);

  protected readonly detail = signal<LessonLearningResponse | null>(null);
  protected readonly selectedOptions = signal<Record<number, number>>({});
  protected readonly result = signal<LessonQuizResultSummary | null>(null);
  protected readonly error = signal<string | null>(null);
  protected readonly saving = signal(false);
  protected readonly currentQuestionIndex = signal(0);

  private enrollmentId = 0;
  private lessonId = 0;

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.enrollmentId = Number(params.get('enrollmentId'));
      this.lessonId = Number(params.get('lessonId'));
      this.currentQuestionIndex.set(0);
      this.selectedOptions.set({});
      this.result.set(null);
      this.error.set(null);
      this.load();
    });
  }

  protected selectOption(questionId: number, optionId: number): void {
    this.selectedOptions.update((current) => ({ ...current, [questionId]: optionId }));
  }

  protected selectedOptionId(questionId: number): number | null {
    return this.selectedOptions()[questionId] ?? null;
  }

  protected questionResult(questionId: number) {
    return this.result()?.questions.find((question) => question.questionId === questionId) ?? null;
  }

  protected questionForResult(item: LessonLearningResponse, questionId: number): LearningQuestion | null {
    return item.lesson.questions.find((question) => question.id === questionId) ?? null;
  }

  protected optionText(question: LearningQuestion, optionId: number | null): string {
    if (optionId === null) {
      return 'Keine Antwort';
    }

    return question.options.find((option) => option.id === optionId)?.text ?? 'Unbekannte Antwort';
  }

  protected currentQuestion(questions: LearningQuestion[]): LearningQuestion | null {
    return questions[this.currentQuestionIndex()] ?? null;
  }

  protected isLastQuestion(questions: LearningQuestion[]): boolean {
    return this.currentQuestionIndex() === questions.length - 1;
  }

  protected goToPreviousQuestion(): void {
    this.error.set(null);
    this.currentQuestionIndex.update((index) => Math.max(0, index - 1));
  }

  protected goToNextQuestion(item: LessonLearningResponse): void {
    const question = this.currentQuestion(item.lesson.questions);
    if (!question || !this.selectedOptionId(question.id)) {
      this.error.set('Bitte beantworte diese Frage, bevor du fortfährst.');
      return;
    }

    this.error.set(null);
    this.currentQuestionIndex.update((index) => Math.min(item.lesson.questions.length - 1, index + 1));
  }

  protected submitQuiz(item: LessonLearningResponse): void {
    const question = this.currentQuestion(item.lesson.questions);
    if (!question || !this.selectedOptionId(question.id)) {
      this.error.set('Bitte beantworte diese Frage, bevor du den Wissens-Check auswertest.');
      return;
    }

    if (!item.lesson.questions.every((question) => Boolean(this.selectedOptionId(question.id)))) {
      this.error.set('Bitte beantworte alle Fragen.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    this.api.submitLessonQuiz(item.enrollment.id, item.lesson.id, {
      answers: item.lesson.questions.map((question) => ({
        questionId: question.id,
        selectedOptionId: this.selectedOptionId(question.id) ?? 0
      }))
    }).subscribe({
      next: (result) => {
        this.result.set(result);
        this.detail.set(result.lesson);
        this.saving.set(false);
      },
      error: () => {
      this.error.set('Der Wissens-Check konnte nicht ausgewertet werden.');
        this.saving.set(false);
      }
    });
  }

  protected answeredCount(questions: LearningQuestion[]): number {
    return questions.filter((question) => Boolean(this.selectedOptionId(question.id))).length;
  }

  private load(): void {
    this.api.learningLesson(this.enrollmentId, this.lessonId).subscribe((detail) => {
      this.detail.set(detail);
      const latestQuizResult = detail.lesson.latestQuizResult;

      if (latestQuizResult) {
        this.result.set(latestQuizResult);
        this.selectedOptions.set(
          latestQuizResult.questions.reduce<Record<number, number>>((selected, question) => {
            selected[question.questionId] = question.selectedOptionId;
            return selected;
          }, {})
        );
      }
    });
  }
}
